// Role-based layout wrapper component
// CREATED: Unified layout system that provides consistent navigation across all roles
// Desktop (>=768px): Shows sidebar for all roles (admin, director, manager, operator)
// Mobile (<768px): Shows role-specific navigation
//   - All roles: Bottom navigation bar with role-appropriate tabs
//   - Admin/Director: Includes "Plus" menu that opens MoreMenuBottomSheet
// Features: Fixed-width sidebar, theme-aware styling, responsive breakpoints aligned with Tailwind
// UPDATED: Extended bottom navigation to Admin/Director with MoreMenuBottomSheet
// UPDATED: Removed IncidentReportButton - incident reporting is now integrated into Incidents page
// UPDATED: Integrated BottomSheetProvider and premium scale effect on main content
// UPDATED: Removed collapsible sidebar feature - sidebar now has fixed width
// UPDATED: Desktop main content wrapper is now a dedicated h-screen overflow-y-auto scroll container
//          to prevent content being cut off on reduced-height screens (instead of relying on body scroll)

import { ReactNode, useState } from 'react';
import { motion, useTransform, useReducedMotion, useMotionValue } from 'framer-motion';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { AppSidebar, NavItem } from './AppSidebar';
import { UnifiedBottomNav } from './UnifiedBottomNav';
import { ProfileBottomSheet } from './ProfileBottomSheet';
import { MoreMenuBottomSheet } from './MoreMenuBottomSheet';
import { NetworkStatusBanner } from './NetworkStatusBanner';
import { PasswordChangeModal } from '../PasswordChangeModal';
import { type UserRole, getRoleTheme } from '../../lib/roleThemes';
import { BottomSheetProvider, useBottomSheetOptional } from '../../contexts/BottomSheetContext';

interface RoleBasedLayoutProps {
  children: ReactNode;
  currentView: string;
  onNavigate: (view: string) => void;
  navItems: NavItem[];
  userName: string;
  userInitials: string;
  userRole: UserRole;
  onLogout: () => void;
  // Optional props for mobile navigation (operator/manager)
  onProfileClick?: () => void;
}

// Internal component that uses the context
function RoleBasedLayoutInner({
  children,
  currentView,
  onNavigate,
  navItems,
  userName,
  userInitials,
  userRole,
  onLogout,
  onProfileClick
}: RoleBasedLayoutProps) {
  const { isDesktop, isMobile } = useResponsiveLayout();
  const shouldReduceMotion = useReducedMotion();

  // State management
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // Bottom sheet context for scale effect
  const bottomSheetContext = useBottomSheetOptional();

  // Create default motion value if context is not available
  const defaultProgress = useMotionValue(0);
  const openProgress = bottomSheetContext?.openProgress || defaultProgress;

  // Transform values for scale effect (always called, hooks rules)
  const contentScale = useTransform(openProgress, [0, 1], [1, 0.97]);
  const contentBorderRadius = useTransform(openProgress, [0, 1], [0, 16]);
  const contentTranslateY = useTransform(openProgress, [0, 1], [0, -10]);

  const handlePasswordChange = () => {
    setShowPasswordModal(true);
  };

  const handleProfileClick = () => {
    if (onProfileClick) {
      onProfileClick();
    }
    setShowProfilePanel(true);
  };

  const handleMoreClick = () => {
    setShowMoreMenu(true);
  };

  const handleMoreMenuNavigate = (path: string) => {
    // Map paths to view IDs
    const pathToViewMap: Record<string, string> = {
      '/users': 'users',
      '/sites': 'sites',
      '/services': 'services',
      '/templates': 'templates'
    };

    const viewId = pathToViewMap[path];
    if (viewId) {
      onNavigate(viewId);
    }
  };

  // Determine navigation type based on role and viewport
  const showDesktopSidebar = isDesktop;
  const showBottomNav = isMobile;

  return (
    <div className="relative min-h-screen bg-gray-50">
      {/* Network Status Banner - All roles */}
      <NetworkStatusBanner />

      {/* Desktop Sidebar - All roles */}
      {showDesktopSidebar && (
        <AppSidebar
          navItems={navItems}
          currentView={currentView}
          onNavigate={onNavigate}
          userName={userName}
          userInitials={userInitials}
          userRole={userRole}
          onPasswordChange={handlePasswordChange}
          onLogout={onLogout}
        />
      )}

      {/* Main Content Area with scale effect */}
      <motion.div
        className={`transition-all duration-300 ${
          showDesktopSidebar ? 'md:ml-64 md:h-screen md:overflow-y-auto' : ''
        }`}
        style={{
          scale: !shouldReduceMotion && isMobile && bottomSheetContext ? contentScale : undefined,
          borderRadius: !shouldReduceMotion && isMobile && bottomSheetContext ? contentBorderRadius : undefined,
          y: !shouldReduceMotion && isMobile && bottomSheetContext ? contentTranslateY : undefined,
          transformOrigin: isMobile ? 'center top' : undefined,
          overflow: isMobile ? 'hidden' : undefined,
          willChange: isMobile ? 'transform' : undefined,
        }}
      >
        <main className={`transition-opacity duration-300 ${
          showDesktopSidebar ? 'px-8 py-8' : 'px-4 py-4'
        } ${showBottomNav ? 'pb-24' : ''}`}>
          {children}
        </main>
      </motion.div>

      {/* Bottom Navigation - All roles on mobile */}
      {showBottomNav && (
        <UnifiedBottomNav
          currentView={currentView}
          onNavigate={onNavigate}
          onProfileClick={handleProfileClick}
          onMoreClick={(userRole === 'admin' || userRole === 'director') ? handleMoreClick : undefined}
          userRole={userRole}
          navItems={navItems}
        />
      )}

      {/* Profile Bottom Sheet - Mobile only */}
      {showBottomNav && (
        <ProfileBottomSheet
          isOpen={showProfilePanel}
          onClose={() => setShowProfilePanel(false)}
          userName={userName}
          userInitials={userInitials}
          userRole={userRole}
          onPasswordChange={handlePasswordChange}
          onLogout={onLogout}
        />
      )}

      {/* More Menu Bottom Sheet - Admin and Director only */}
      {showBottomNav && (userRole === 'admin' || userRole === 'director') && (
        <MoreMenuBottomSheet
          isOpen={showMoreMenu}
          onClose={() => setShowMoreMenu(false)}
          onNavigate={handleMoreMenuNavigate}
          currentPath={`/${currentView}`}
        />
      )}

      {/* Password Change Modal - All roles */}
      <PasswordChangeModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  );
}

// Exported component with provider
export function RoleBasedLayout(props: RoleBasedLayoutProps) {
  return (
    <BottomSheetProvider>
      <RoleBasedLayoutInner {...props} />
    </BottomSheetProvider>
  );
}
